import React from 'react'
import MuiTextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import BaseDisplayItem, {
    BaseDisplayItemProps,
    DisplayItemBase,
} from '@/feature/DisplayItems/BaseDisplayItem'

/* ---------- Types ---------- */

type AmountType = 'number' | 'currency' | 'percentage'

interface AmountSettings {
    valueType?: AmountType
    currency?: keyof typeof currencySymbols
    decimals?: number
}

interface AmountItem extends DisplayItemBase {
    def_dossier_def_field?: {
        def_field?: {
            settings?: AmountSettings
        }
    }
}

interface Props extends BaseDisplayItemProps<string, AmountItem> {
    kedo: { t: (key: string) => string }
    onKeyPress?: (
        id: string | number,
        e: React.KeyboardEvent<HTMLInputElement>
    ) => void
}

/* ---------- Currency ---------- */

const currencySymbols = {
    USD: '$',
    COP: '$',
    EUR: '€',
    CRC: '₡',
    GBP: '£',
    ILS: '₪',
    INR: '₹',
    JPY: '¥',
    KRW: '₩',
    NGN: '₦',
    PHP: '₱',
    PLN: 'zł',
    PYG: '₲',
    THB: '฿',
    UAH: '₴',
    VND: '₫',
} as const

/* ---------- Component ---------- */

class AmountField extends BaseDisplayItem<
    string,
    AmountItem,
    HTMLInputElement
> {
    constructor(props: Props) {
        super(props)
        this.onChange = this.onChange.bind(this)
        this.onKeyPressNumber = this.onKeyPressNumber.bind(this)
        this.onKeyPressCurrency = this.onKeyPressCurrency.bind(this)
        this.onKeyPressPercentage = this.onKeyPressPercentage.bind(this)
    }

    /* ---------- Settings ---------- */

    private getAmountType(): AmountType {
        return (
            this.getSetting<AmountType>('valueType', {
                defaultValue: 'number',
            }) ?? 'number'
        )
    }

    private getCurrencySign(): string {
        const cur = this.getSetting<keyof typeof currencySymbols>('currency')
        if (!cur) return ''
        return currencySymbols[cur] ?? ''
    }

    /* ---------- Common ---------- */

    private renderUnknown() {
        const props = this.props as Props
        return (
            <span className="text-muted small">{props.kedo.t('Unknown')}</span>
        )
    }

    private onChange(e: React.ChangeEvent<HTMLInputElement>) {
        this.updateValue(e.target.value)
        ;(this.props as Props).changeValue?.(
            (this.props as Props).item.id,
            e.target.value,
            (this.props as Props).item
        )
        // Note: BaseDisplayItem.updateValue already calls changeValue(id, value, item)
        // but your Base signature includes item? so this keeps legacy behavior.
        // If you update BaseDisplayItem.changeValue to always pass item, you can delete this.
    }

    /* ---------- Key filters (ported behavior) ---------- */

    private onKeyPressNumber(evt: React.KeyboardEvent<HTMLInputElement>) {
        const key = evt.key
        const value = (evt.currentTarget.value ?? '') as string

        // Allow navigation keys
        if (
            key === 'Backspace' ||
            key === 'Delete' ||
            key === 'ArrowLeft' ||
            key === 'ArrowRight'
        ) {
            return
        }

        const isMinus = key === '-' && value.length === 0

        const regex = /^[0-9.,\- ]+$/
        if (
            !regex.test(key) ||
            (key === '-' && value.includes('-') && !isMinus)
        ) {
            evt.preventDefault()
        }
    }

    private onKeyPressCurrency(evt: React.KeyboardEvent<HTMLInputElement>) {
        const key = evt.key

        // Allow navigation keys
        if (
            key === 'Backspace' ||
            key === 'Delete' ||
            key === 'ArrowLeft' ||
            key === 'ArrowRight'
        ) {
            return
        }

        const regex = /^[0-9., ]+$/
        if (!regex.test(key)) {
            evt.preventDefault()
        }
    }

    private onKeyPressPercentage(evt: React.KeyboardEvent<HTMLInputElement>) {
        // preserve legacy: prevent "e" for number inputs
        if (evt.key === 'e') evt.preventDefault()
    }

    /* ---------- Render: SHOW ---------- */

    private renderShowNumber() {
        if (!this.state.value || String(this.state.value).length <= 0) {
            return this.renderUnknown()
        }
        return this.state.value
    }

    private renderShowCurrency() {
        if (!this.state.value || String(this.state.value).length <= 0) {
            return this.renderUnknown()
        }

        const sign = this.getCurrencySign()
        const props = this.props as Props
        const value = props.value ?? ''

        // legacy behavior: just prefix sign
        return `${sign}${value}`
    }

    private renderShowPercentage() {
        if (!this.state.value || String(this.state.value).length <= 0) {
            return this.renderUnknown()
        }
        return `${this.state.value} %`
    }

    renderShow(): React.ReactNode {
        const type = this.getAmountType()
        switch (type) {
            case 'currency':
                return this.renderShowCurrency()
            case 'percentage':
                return this.renderShowPercentage()
            case 'number':
            default:
                return this.renderShowNumber()
        }
    }

    /* ---------- Render: EDIT ---------- */

    private renderEditNumber() {
        return (
            <MuiTextField
                size="small"
                fullWidth
                inputRef={this.innerRef}
                value={this.state.value ?? ''}
                onChange={this.onChange}
                onKeyDown={this.onKeyPressNumber}
                error={this.isInvalid()}
                helperText={this.isInvalid() ? this.getErrorText() : ''}
                type="text"
            />
        )
    }

    private renderEditCurrency() {
        const sign = this.getCurrencySign()

        return (
            <MuiTextField
                size="small"
                fullWidth
                inputRef={this.innerRef}
                value={this.state.value ?? ''}
                onChange={this.onChange}
                onKeyDown={this.onKeyPressCurrency}
                error={this.isInvalid()}
                helperText={this.isInvalid() ? this.getErrorText() : ''}
                placeholder="0,00"
                type="text"
                InputProps={{
                    startAdornment: sign ? (
                        <InputAdornment position="start">{sign}</InputAdornment>
                    ) : undefined,
                }}
            />
        )
    }

    private renderEditPercentage() {
        return (
            <MuiTextField
                size="small"
                fullWidth
                inputRef={this.innerRef}
                value={this.state.value ?? ''}
                onChange={this.onChange}
                onKeyDown={this.onKeyPressPercentage}
                error={this.isInvalid()}
                helperText={this.isInvalid() ? this.getErrorText() : ''}
                type="number"
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                    ),
                }}
            />
        )
    }

    renderEdit(): React.ReactNode {
        const type = this.getAmountType()
        switch (type) {
            case 'currency':
                return this.renderEditCurrency()
            case 'percentage':
                return this.renderEditPercentage()
            case 'number':
            default:
                return this.renderEditNumber()
        }
    }

    /* ---------- Render: SEARCH ---------- */

    renderSearch(): React.ReactNode {
        const props = this.props as Props

        return (
            <MuiTextField
                size="small"
                fullWidth
                type="text"
                value={props.value ?? ''}
                onChange={(e) =>
                    props.changeValue?.(
                        props.item.id,
                        e.target.value,
                        props.item
                    )
                }
                onKeyPress={
                    props.onKeyPress
                        ? (e) => props.onKeyPress!(props.item.id, e)
                        : undefined
                }
            />
        )
    }
}

export default AmountField
